import { useState, useRef, useEffect } from "react";
import { Sparkles, Send, Loader2, Trash2, Bot, User, Volume2, Square, AudioLines } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import Reveal from "@/components/Reveal";
import { SectionLabel } from "@/components/common";
import { streamChat } from "@/lib/streamChat";
import { synthesizeSpeech } from "@/lib/voice";
import VoiceMode from "@/components/VoiceMode";
import { toast } from "sonner";

const SUGGESTIONS = [
  "What does Vvvoraa do?",
  "What AI solutions do you offer?",
  "How can I work with Vvvoraa?",
  "Are you hiring?",
  "How can I contact your team?",
];

function makeSession() {
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
}

export default function AIEnquiry() {
  const [sessionId, setSessionId] = useState(makeSession);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [speakingIdx, setSpeakingIdx] = useState(null);
  const [speakingLoading, setSpeakingLoading] = useState(null);
  const audioRef = useRef(null);
  const endRef = useRef(null);
  const [voiceOpen, setVoiceOpen] = useState(false);

  const speak = async (text, idx) => {
    if (!text || !text.trim()) return;
    if (speakingIdx === idx) {
      audioRef.current?.pause();
      setSpeakingIdx(null);
      return;
    }
    setSpeakingLoading(idx);
    try {
      const url = await synthesizeSpeech(text);
      audioRef.current?.pause();
      const audio = new Audio(url);
      audioRef.current = audio;
      setSpeakingIdx(idx);
      audio.onended = () => setSpeakingIdx(null);
      await audio.play();
    } catch (e) {
      toast.error(e.message || "Voice playback failed.");
    } finally {
      setSpeakingLoading(null);
    }
  };

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, loading]);

  const ask = async (text) => {
    const q = (text ?? input).trim();
    if (!q || loading) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: q }]);
    setLoading(true);
    setMessages((m) => [...m, { role: "ai", content: "" }]);
    try {
      await streamChat({
        sessionId,
        message: q,
        onChunk: (full) => {
          setMessages((m) => {
            const copy = [...m];
            copy[copy.length - 1] = { role: "ai", content: full };
            return copy;
          });
        },
      });
    } catch (err) {
      const msg = err.message || "The assistant is unavailable right now.";
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "ai", content: msg };
        return copy;
      });
      toast.error("AI assistant error");
    } finally {
      setLoading(false);
    }
  };

  const clear = () => { setMessages([]); setSessionId(makeSession()); };

  return (
    <div className="relative min-h-[80vh] vora-hero-radial">
      <div className="absolute inset-0 vora-grid-bg opacity-40" aria-hidden />
      <section className="relative mx-auto max-w-3xl px-5 sm:px-8 py-24">
        <Reveal>
          <SectionLabel>AI Enquiry</SectionLabel>
          <h1 className="mt-4 text-4xl sm:text-5xl font-bold tracking-tight">Ask Vvvoraa AI</h1>
          <p className="mt-4 text-lg text-slate-300 max-w-xl">Ask anything about Vvvoraa — our products, AI solutions, careers or how to get in touch.</p>
        </Reveal>

        <Reveal delay={0.08}>
          <div className="mt-8 vora-glass rounded-3xl flex flex-col h-[560px]" data-testid="ai-chat">
            <div className="flex items-center justify-between px-5 py-3.5 border-b border-white/8">
              <span className="inline-flex items-center gap-2 text-sm text-white"><span className="flex h-7 w-7 items-center justify-center rounded-lg bg-gradient-to-br from-[#4F8CFF] to-[#7C5CFF]"><Sparkles className="h-4 w-4 text-white" /></span> Vvvoraa AI</span>
              <Button size="sm" variant="ghost" onClick={clear} className="text-slate-400 hover:text-white hover:bg-white/5 rounded-full gap-1.5" data-testid="ai-clear-btn"><Trash2 className="h-3.5 w-3.5" /> Clear</Button>
            </div>

            <div className="flex-1 overflow-y-auto p-5 space-y-4">
              {messages.length === 0 && (
                <div className="h-full flex flex-col items-center justify-center text-center">
                  <Bot className="h-10 w-10 text-[#4F8CFF]" />
                  <p className="mt-4 text-sm text-slate-400 max-w-sm">Start a conversation or pick a suggested question below.</p>
                  <div className="mt-5 flex flex-wrap justify-center gap-2 max-w-lg">
                    {SUGGESTIONS.map((s) => (
                      <button key={s} onClick={() => ask(s)} className="rounded-full border border-white/12 bg-white/[0.03] px-3.5 py-1.5 text-xs text-slate-300 hover:text-white hover:border-[#4F8CFF]/40 transition-colors" data-testid="ai-suggestion">{s}</button>
                    ))}
                  </div>
                </div>
              )}
              {messages.map((m, i) => {
                if (m.role === "ai" && m.content === "" && i === messages.length - 1) return null;
                return (
                  <div key={i} className={`flex gap-3 ${m.role === "user" ? "flex-row-reverse" : ""}`} data-testid={`ai-msg-${m.role}`}>
                    <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${m.role === "user" ? "bg-white/10 text-white" : "bg-[#4F8CFF]/15 text-[#4F8CFF]"}`}>
                      {m.role === "user" ? <User className="h-4 w-4" /> : <Bot className="h-4 w-4" />}
                    </span>
                    <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${m.role === "user" ? "bg-[#4F8CFF] text-white" : "bg-white/[0.04] text-slate-200 border border-white/8"}`}>
                      {m.content}
                      {m.role === "ai" && m.content && (
                        <button
                          onClick={() => speak(m.content, i)}
                          className="mt-1.5 inline-flex items-center gap-1 text-[11px] text-slate-500 hover:text-[#4F8CFF] transition-colors"
                          data-testid={`ai-speak-${i}`}
                          aria-label="Read aloud"
                        >
                          {speakingLoading === i ? <Loader2 className="h-3 w-3 animate-spin" /> : speakingIdx === i ? <Square className="h-3 w-3" /> : <Volume2 className="h-3 w-3" />}
                          {speakingIdx === i ? "Stop" : "Listen"}
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
              {loading && (messages.length === 0 || messages[messages.length - 1]?.content === "") && (
                <div className="flex gap-3" data-testid="ai-typing">
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#4F8CFF]/15 text-[#4F8CFF]"><Bot className="h-4 w-4" /></span>
                  <div className="rounded-2xl px-4 py-3 bg-white/[0.04] border border-white/8">
                    <span className="flex gap-1">
                      <span className="h-2 w-2 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: "0ms" }} />
                      <span className="h-2 w-2 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: "150ms" }} />
                      <span className="h-2 w-2 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: "300ms" }} />
                    </span>
                  </div>
                </div>
              )}
              <div ref={endRef} />
            </div>

            <form onSubmit={(e) => { e.preventDefault(); ask(); }} className="flex items-center gap-2 p-4 border-t border-white/8">
              <Input value={input} onChange={(e) => setInput(e.target.value)} placeholder="Ask Vvvoraa AI…" className="bg-white/[0.03] border-white/12 text-white placeholder:text-slate-500 focus-visible:ring-[#4F8CFF]" data-testid="ai-input" />
              <Button type="button" onClick={() => setVoiceOpen(true)} disabled={loading} className="bg-[#7C5CFF] hover:bg-[#6a4de0] text-white rounded-full h-10 w-10 p-0 shrink-0" data-testid="ai-voice-mode-btn" aria-label="Start voice mode">
                <AudioLines className="h-4 w-4" />
              </Button>
              <Button type="submit" disabled={loading || !input.trim()} className="bg-[#4F8CFF] hover:bg-[#3f7af0] text-white rounded-full h-10 w-10 p-0 shrink-0" data-testid="ai-send-btn">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              </Button>
            </form>
          </div>
          <p className="mt-3 text-center text-xs text-slate-500">Powered by Vvvoraa AI. Answers are generated and may be imprecise.</p>
        </Reveal>
      </section>

      {voiceOpen && (
        <VoiceMode
          sessionId={sessionId}
          onTurn={(u, a) => setMessages((m) => [...m, { role: "user", content: u }, { role: "ai", content: a }])}
          onClose={() => setVoiceOpen(false)}
        />
      )}
    </div>
  );
}
